import { useEffect, useMemo, useState, useCallback } from 'react';
// routes
import { paths } from 'src/routes/paths';
// locales
import { useLocales } from 'src/locales';
// components
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import SvgColor from 'src/components/svg-color';
// auth
import { authApiService } from 'src/api/auth-api';
import { STORAGE_KEYS } from 'src/constants/api';
// types
import { HTMenu } from 'src/types/api-response';

// ----------------------------------------------------------------------

const icon = (name: string) => (
  <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
);

const ICONS = {
  job: icon('ic_job'),
  blog: icon('ic_blog'),
  chat: icon('ic_chat'),
  mail: icon('ic_mail'),
  user: icon('ic_user'),
  file: icon('ic_file'),
  lock: icon('ic_lock'),
  tour: icon('ic_tour'),
  order: icon('ic_order'),
  label: icon('ic_label'),
  blank: icon('ic_blank'),
  kanban: icon('ic_kanban'),
  folder: icon('ic_folder'),
  banking: icon('ic_banking'),
  booking: icon('ic_booking'),
  invoice: icon('ic_invoice'),
  product: icon('ic_product'),
  calendar: icon('ic_calendar'),
  disabled: icon('ic_disabled'),
  external: icon('ic_external'),
  menuItem: icon('ic_menu_item'),
  ecommerce: icon('ic_ecommerce'),
  analytics: icon('ic_analytics'),
  dashboard: icon('ic_dashboard'),
  leaf: icon('ic_file'), // Icon cho item cuối cùng (không có children)
};

// ----------------------------------------------------------------------

// Định nghĩa type NavItem bên ngoài hook
type NavItem = {
  title: string;
  path: string;
  icon?: JSX.Element;
  children?: NavItem[];
  disabled?: boolean; // Flag để biết item này có thể click hay không
};

export function useNavData() {
  const { t } = useLocales();
  const mapMenusToItems = useCallback((menus: HTMenu[] | undefined): NavItem[] => {
    if (!menus || menus.length === 0) return [];

    const mappedItems = (menus as HTMenu[])
      .filter((m) => m.hienThi !== false)
      .map((m): NavItem => {
        const children: NavItem[] = mapMenusToItems((m.children as unknown as HTMenu[]) || []);
        const hasChildren = children && children.length > 0;

        const resolvedPath = hasChildren ? '#' : (m.tenArea || '#');

        const base: NavItem = {
          title: m.ten || 'Menu',
          path: resolvedPath,
          icon: hasChildren ? ICONS.menuItem : ICONS.leaf,
        };

        return hasChildren ? { ...base, children } : base;
      });

    // Thêm dashboard vào đầu danh sách
    return [...mappedItems];
  }, []);

  const [dynamicSections, setDynamicSections] = useState<any[]>([]);

useEffect(() => {
  const loadMenus = async () => {
    try {
      const menuStr = localStorage.getItem(STORAGE_KEYS.MENU_ITEMS);
      let menus: HTMenu[] = [];

      if (menuStr) {
        menus = JSON.parse(menuStr || '[]');
      } else {
        const res = await authApiService.getCurrentUser();
        menus = (res?.oneItem as any)?.htMenuModels || [];
      }
      
      const dashboardItem: NavItem = {
        title: 'Dashboard',
        path: '/dashboard',
        icon: ICONS.dashboard || ICONS.menuItem,
      };
  
      // Section Dashboard cố định
      const dashboardSection = {
        subheader: 'Tổng quan',
        items: [dashboardItem],
      };

      // Build sections từ menu API
      const menuSections = (menus || [])
        .filter((m) => m.hienThi !== false)
        .map((m) => ({
          subheader: m.ten || 'Menu',
          items: mapMenusToItems((m.children as unknown as HTMenu[]) || []),
        }));

      setDynamicSections([dashboardSection, ...menuSections]);
    } catch (e) {
      console.error('Error loading menus:', e);
      setDynamicSections([]);
    }
  };

  loadMenus();
}, [mapMenusToItems]);;

  return [...dynamicSections];
}